type Props = { value: string; size?: number; title?: string };

const VERSION = 4;
const MODULE_COUNT = 17 + VERSION * 4;
const DATA_CODEWORDS = 80;
const ECC_CODEWORDS = 20;

const gfExp = new Array<number>(512).fill(0);
const gfLog = new Array<number>(256).fill(0);
let gfValue = 1;
for (let index = 0; index < 255; index += 1) {
  gfExp[index] = gfValue;
  gfLog[gfValue] = index;
  gfValue <<= 1;
  if (gfValue & 0x100) gfValue ^= 0x11d;
}
for (let index = 255; index < 512; index += 1) gfExp[index] = gfExp[index - 255];

function gfMultiply(left: number, right: number) {
  if (!left || !right) return 0;
  return gfExp[gfLog[left] + gfLog[right]];
}

function generatorPolynomial(degree: number) {
  let polynomial = [1];
  for (let exponent = 0; exponent < degree; exponent += 1) {
    const next = new Array<number>(polynomial.length + 1).fill(0);
    polynomial.forEach((coefficient, index) => {
      next[index] ^= coefficient;
      next[index + 1] ^= gfMultiply(coefficient, gfExp[exponent]);
    });
    polynomial = next;
  }
  return polynomial;
}

function errorCorrection(data: number[], count: number) {
  const generator = generatorPolynomial(count);
  const remainder = new Array<number>(count).fill(0);
  data.forEach((byte) => {
    const factor = byte ^ remainder[0];
    remainder.shift();
    remainder.push(0);
    for (let index = 0; index < count; index += 1) remainder[index] ^= gfMultiply(generator[index + 1], factor);
  });
  return remainder;
}

function pushBits(target: number[], value: number, length: number) {
  for (let shift = length - 1; shift >= 0; shift -= 1) target.push((value >>> shift) & 1);
}

function dataCodewords(value: string) {
  const bytes = [...new TextEncoder().encode(value)];
  if (bytes.length > 78) throw new Error("Adresse trop longue pour le QR code intégré");
  const bits: number[] = [];
  pushBits(bits, 0b0100, 4);
  pushBits(bits, bytes.length, 8);
  bytes.forEach((byte) => pushBits(bits, byte, 8));
  const capacityBits = DATA_CODEWORDS * 8;
  for (let index = 0; index < Math.min(4, capacityBits - bits.length); index += 1) bits.push(0);
  while (bits.length % 8) bits.push(0);
  const result: number[] = [];
  for (let index = 0; index < bits.length; index += 8) {
    let byte = 0;
    for (let offset = 0; offset < 8; offset += 1) byte = (byte << 1) | bits[index + offset];
    result.push(byte);
  }
  let pad = 0;
  while (result.length < DATA_CODEWORDS) {
    result.push(pad % 2 === 0 ? 0xec : 0x11);
    pad += 1;
  }
  return result;
}

function formatBits(mask: number) {
  const data = (0b01 << 3) | mask;
  let remainder = data;
  for (let index = 0; index < 10; index += 1) remainder = (remainder << 1) ^ (((remainder >>> 9) & 1) ? 0x537 : 0);
  return ((data << 10) | remainder) ^ 0x5412;
}

function qrMatrix(value: string) {
  const size = MODULE_COUNT;
  const modules = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  const reserved = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));

  const setFunction = (row: number, column: number, dark: boolean) => {
    if (row < 0 || column < 0 || row >= size || column >= size) return;
    modules[row][column] = dark;
    reserved[row][column] = true;
  };

  const finder = (top: number, left: number) => {
    for (let row = -1; row <= 7; row += 1) {
      for (let column = -1; column <= 7; column += 1) {
        const inside = row >= 0 && row <= 6 && column >= 0 && column <= 6;
        const dark = inside && (row === 0 || row === 6 || column === 0 || column === 6 || (row >= 2 && row <= 4 && column >= 2 && column <= 4));
        setFunction(top + row, left + column, dark);
      }
    }
  };

  finder(0, 0);
  finder(0, size - 7);
  finder(size - 7, 0);

  for (let index = 8; index < size - 8; index += 1) {
    setFunction(6, index, index % 2 === 0);
    setFunction(index, 6, index % 2 === 0);
  }

  const alignmentCenter = 26;
  for (let row = -2; row <= 2; row += 1) {
    for (let column = -2; column <= 2; column += 1) {
      const distance = Math.max(Math.abs(row), Math.abs(column));
      setFunction(alignmentCenter + row, alignmentCenter + column, distance !== 1);
    }
  }

  const format = formatBits(0);
  const formatBit = (index: number) => ((format >>> index) & 1) !== 0;
  for (let index = 0; index <= 5; index += 1) setFunction(index, 8, formatBit(index));
  setFunction(7, 8, formatBit(6));
  setFunction(8, 8, formatBit(7));
  setFunction(8, 7, formatBit(8));
  for (let index = 9; index < 15; index += 1) setFunction(8, 14 - index, formatBit(index));
  for (let index = 0; index < 8; index += 1) setFunction(8, size - 1 - index, formatBit(index));
  for (let index = 8; index < 15; index += 1) setFunction(size - 15 + index, 8, formatBit(index));
  setFunction(size - 8, 8, true);

  const data = dataCodewords(value);
  const codewords = [...data, ...errorCorrection(data, ECC_CODEWORDS)];
  const dataBits: number[] = [];
  codewords.forEach((byte) => pushBits(dataBits, byte, 8));

  let bitIndex = 0;
  let upward = true;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right -= 1;
    for (let vertical = 0; vertical < size; vertical += 1) {
      const row = upward ? size - 1 - vertical : vertical;
      for (let offset = 0; offset < 2; offset += 1) {
        const column = right - offset;
        if (reserved[row][column]) continue;
        let dark = bitIndex < dataBits.length ? dataBits[bitIndex] === 1 : false;
        if ((row + column) % 2 === 0) dark = !dark;
        modules[row][column] = dark;
        bitIndex += 1;
      }
    }
    upward = !upward;
  }

  return modules;
}

export function QrCode({ value, size = 220, title = "QR code du QCM" }: Props) {
  let matrix: boolean[][];
  try { matrix = qrMatrix(value); }
  catch { return <div className="qcm-qr-fallback">QR indisponible<br /><small>{value}</small></div>; }
  const quiet = 4;
  const viewSize = matrix.length + quiet * 2;
  const cells = matrix.flatMap((row, rowIndex) => row.flatMap((dark, columnIndex) => dark ? [`M${columnIndex + quiet} ${rowIndex + quiet}h1v1h-1z`] : []));
  return <svg width={size} height={size} viewBox={`0 0 ${viewSize} ${viewSize}`} role="img" aria-label={title} shapeRendering="crispEdges">
    <rect width={viewSize} height={viewSize} fill="white" />
    <path d={cells.join("")} fill="black" />
  </svg>;
}
